import { ArrowRight, Target, Layout, Rocket, DollarSign, TrendingUp, Eye, Music, Instagram, Youtube, Radio, Mic } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';

const FrameworkCard = ({ num, icon: Icon, title, description, items, note }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-8 group hover:border-primary/50 hover:shadow-lg transition-all duration-300">
    <div className="flex items-center gap-4 mb-6">
      <span className="font-oswald text-4xl font-bold text-gray-100">0{num}</span>
      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
        <Icon className="w-6 h-6 text-primary" />
      </div>
    </div>
    <h3 className="font-oswald uppercase text-xl md:text-2xl font-bold text-gray-900">
      {title}
    </h3>
    <p className="font-manrope text-sm text-gray-500 mt-3">
      {description}
    </p>
    <ul className="mt-4 space-y-2">
      {items.map((item, idx) => (
        <li key={idx} className="font-manrope text-sm text-gray-700 flex items-start gap-2">
          <span className="text-primary mt-1">•</span>
          {item}
        </li>
      ))}
    </ul>
    {note && (
      <p className="font-manrope text-sm text-primary mt-4 pt-4 border-t border-gray-100 italic">
        {note}
      </p>
    )}
  </div>
);

export default function ArtistManagement() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Hero Section */}
      <section
        data-testid="artist-hero"
        className="relative pt-32 pb-24 md:pt-40 md:pb-32 bg-gradient-to-br from-white via-green-50/30 to-white overflow-hidden"
      >
        <div className="absolute top-20 right-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            <span className="font-manrope uppercase tracking-widest text-xs font-semibold text-primary">
              Artist Management
            </span>
            <h1
              className="font-oswald uppercase tracking-tight font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] mt-4"
              data-testid="artist-headline"
            >
              <span className="text-gray-900">We Don't Manage Artists.</span>
              <br />
              <span className="text-primary">We Build Cultural Assets.</span>
            </h1>
            <p className="font-manrope text-base md:text-lg text-gray-600 mt-6 max-w-2xl leading-relaxed">
              From music launches to long-term equity, we build artist brands that grow across TikTok, Instagram, YouTube, streaming platforms, and live ecosystems.
            </p>

            {/* CTA */}
            <div className="mt-10">
              <a
                href="https://calendly.com/atabdullah-mah/30min"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="artist-cta-button"
              >
                <Button className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-bold uppercase tracking-wide btn-scale glow-primary flex items-center gap-2">
                  Book a Strategy Call
                  <ArrowRight size={20} />
                </Button>
              </a>
              <p className="font-manrope text-sm text-gray-500 mt-4">
                30 minutes clarity call. No pressure. Just direction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section
        data-testid="artist-approach"
        className="py-24 md:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-16">
            <span className="font-manrope uppercase tracking-widest text-xs font-semibold text-primary">
              Our Approach
            </span>
            <h2 className="font-oswald uppercase tracking-tight font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight mt-4">
              <span className="text-gray-900">The Loose Leaf</span>
              <br />
              <span className="text-primary">Artist Framework™</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Card 1 */}
            <FrameworkCard
              num={1}
              icon={Target}
              title="Identity Before Virality"
              description="We define:"
              items={[
                "Artist positioning",
                "Cultural angle",
                "Audience archetype",
                "Visual & narrative language"
              ]}
              note="No random content. No trend chasing without strategy."
            />

            {/* Card 2 */}
            <FrameworkCard
              num={2}
              icon={Layout}
              title="Platform Strategy"
              description="We build structured growth across:"
              items={[
                "TikTok (Short-form virality engine)",
                "Instagram (Community + brand positioning)",
                "YouTube (Long-form authority + monetisation)",
                "Spotify & Streaming (Consumption + playlist strategy)",
                "LIVE (TikTok LIVE / YouTube Live / Events)",
                "Shorts ecosystem"
              ]}
              note="Every platform has a role. Nothing is accidental."
            />

            {/* Card 3 */}
            <FrameworkCard
              num={3}
              icon={Rocket}
              title="Launch Architecture"
              description="For every release we design:"
              items={[
                "Pre-release hype strategy",
                "Creator seeding",
                "Short-form content bursts",
                "Paid amplification",
                "Playlist positioning strategy",
                "Influencer collaborations",
                "LIVE launch moment",
                "Press & PR angles"
              ]}
              note="We don't 'drop songs.' We engineer moments."
            />

            {/* Card 4 */}
            <FrameworkCard
              num={4}
              icon={DollarSign}
              title="Monetisation Roadmap"
              description="Revenue streams structured intentionally:"
              items={[
                "Streaming",
                "LIVE gifting",
                "Brand partnerships",
                "Sync licensing",
                "Digital products",
                "Events",
                "Merch",
                "Subscription communities"
              ]}
              note="We build recurring revenue — not random payouts."
            />

            {/* Card 5 */}
            <FrameworkCard
              num={5}
              icon={TrendingUp}
              title="Long-Term Equity"
              description="We structure:"
              items={[
                "Personal brand value",
                "IP ownership",
                "Negotiation leverage",
                "Catalog growth",
                "Global Market expansion"
              ]}
              note="Artists should build assets, not just followers."
            />
          </div>
        </div>
      </section>

      {/* Why Loose Leaf Section */}
      <section
        data-testid="artist-why"
        className="py-24 md:py-32 bg-gray-50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 md:gap-16 items-center">
            <div>
              <span className="font-manrope uppercase tracking-widest text-xs font-semibold text-primary">
                Why Loose Leaf Media
              </span>
              <h2 className="font-oswald uppercase tracking-tight font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight mt-4">
                <span className="text-gray-900">Built by</span>
                <br />
                <span className="text-primary">Platform Insiders.</span>
              </h2>
              <p className="font-manrope text-base md:text-lg text-gray-600 mt-6 leading-relaxed">
                Our team has worked inside major platforms and across major digital ecosystems.
              </p>
              <p className="font-manrope text-base md:text-lg text-gray-800 mt-4 leading-relaxed font-medium">
                We understand:
              </p>
              <ul className="mt-4 space-y-3">
                <li className="font-manrope text-base text-gray-700 flex items-start gap-3">
                  <span className="text-primary mt-1">•</span>
                  Algorithm mechanics
                </li>
                <li className="font-manrope text-base text-gray-700 flex items-start gap-3">
                  <span className="text-primary mt-1">•</span>
                  Content psychology
                </li>
                <li className="font-manrope text-base text-gray-700 flex items-start gap-3">
                  <span className="text-primary mt-1">•</span>
                  Platform monetisation tools
                </li>
                <li className="font-manrope text-base text-gray-700 flex items-start gap-3">
                  <span className="text-primary mt-1">•</span>
                  Creator growth patterns
                </li>
                <li className="font-manrope text-base text-gray-700 flex items-start gap-3">
                  <span className="text-primary mt-1">•</span>
                  Regional consumption behavior
                </li>
              </ul>
              <p className="font-manrope text-lg text-primary mt-6 font-semibold">
                We don't guess. We've seen the backend.
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-xl p-8 hover:border-primary/50 hover:shadow-lg transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mic className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-oswald uppercase text-lg font-bold text-gray-900">
                      Creator-Musicians
                    </h3>
                    <p className="font-manrope text-sm text-gray-500 mt-2">
                      Monetising via LIVE and building sustainable income streams
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 hover:border-primary/50 hover:shadow-lg transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Music className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-oswald uppercase text-lg font-bold text-gray-900">
                      Established Artists
                    </h3>
                    <p className="font-manrope text-sm text-gray-500 mt-2">
                      Needing structured digital strategy for modern platforms
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-xl p-8">
                <p className="font-oswald uppercase text-xl font-bold text-gray-900 text-center">
                  This is strategic partnership.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        data-testid="artist-final-cta"
        className="py-24 md:py-32 bg-gradient-to-br from-primary/5 via-white to-primary/5 relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-oswald uppercase tracking-tight font-bold text-3xl sm:text-4xl md:text-5xl text-gray-900">
            Ready to build your
            <span className="text-primary"> cultural asset?</span>
          </h2>
          <p className="font-manrope text-base md:text-lg text-gray-500 mt-4 max-w-xl mx-auto">
            Let's discuss how we can turn your artistry into a long-term brand and business.
          </p>
          <div className="mt-8">
            <a
              href="https://calendly.com/atabdullah-mah/30min"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="artist-final-cta-button"
            >
              <Button className="bg-primary text-white hover:bg-primary/90 rounded-full px-10 py-7 text-lg font-bold uppercase tracking-wide btn-scale glow-primary inline-flex items-center gap-3">
                Book a Strategy Call
                <ArrowRight size={20} />
              </Button>
            </a>
            <p className="font-manrope text-sm text-gray-500 mt-4">
              30 minutes clarity call. No pressure. Just direction.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
